import React, { useRef } from "react";
import { Drawer } from "vaul";

import "./BottomSheet.css";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Hides the title visually but keeps it for screen readers. */
  hideTitle?: boolean;
  /** Right-hand action in the header. Defaults to a "Done" button. */
  action?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Bottom sheet — the mobile counterpart to a popover or a side rail.
 *
 * Built on vaul (already a dependency, and what the tab bar's "More" menu
 * uses) rather than hand-rolled CSS, because a sheet has to get several
 * things right that are easy to miss: it must sit above the fixed tab bar,
 * lock the page behind it, restore focus on close, respect the home-indicator
 * inset, and stay within the viewport when the content is taller than the
 * screen. An earlier CSS-only version of this got the last two wrong — its
 * lower controls rendered past the bottom of the screen, behind the tab bar.
 */
export const BottomSheet: React.FC<Props> = ({
  open,
  onOpenChange,
  title,
  hideTitle,
  action,
  children,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  return (
  <Drawer.Root open={open} onOpenChange={onOpenChange}>
    <Drawer.Portal>
      <Drawer.Overlay className="bottom-sheet__overlay" />
      <Drawer.Content
        className="bottom-sheet"
        /* The library marks #root aria-hidden while the sheet is open. If
           focus stayed on the trigger — which lives inside #root — it would
           sit in a subtree hidden from assistive technology, and the browser
           blocks that ("Blocked aria-hidden on an element because its
           descendant retained focus"). Focus moves into the sheet instead.
           `preventDefault` stops the default focus landing on the first
           option, which on iOS can scroll the sheet unexpectedly; closing
           still returns focus to the trigger. */
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          bodyRef.current?.focus();
        }}
      >
        <div className="bottom-sheet__grabber" aria-hidden />

        <div className="bottom-sheet__head">
          <Drawer.Title className={hideTitle ? "visually-hidden" : "bottom-sheet__title"}>
            {title}
          </Drawer.Title>
          {action ?? (
            <button type="button" className="bottom-sheet__done" onClick={() => onOpenChange(false)}>
              Done
            </button>
          )}
        </div>

        <div className="bottom-sheet__body" ref={bodyRef} tabIndex={-1}>
          {children}
        </div>
      </Drawer.Content>
    </Drawer.Portal>
    </Drawer.Root>
  );
};
